"use client";

import React, { useState, useEffect } from "react";
import { Phone, Mail, Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="29903" className="hidden lg:flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-primary/10 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent text-sm font-bold font-serif uppercase tracking-widest text-foreground hover:text-secondary transition-colors h-auto p-0">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[220px] gap-1 p-2 bg-primary-900 rounded-[5px] shadow-2xl border border-primary/20">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-[5px] p-3 leading-none no-underline outline-none transition-colors hover:bg-primary/10 hover:text-secondary focus:bg-primary/10 focus:text-secondary text-xs font-serif uppercase tracking-wider text-foreground">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="text-sm font-bold font-serif uppercase tracking-widest text-foreground hover:text-secondary hover:underline hover:underline-offset-8 hover:decoration-secondary/40 transition-all block">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <nav className="flex flex-col space-y-4 p-6">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-primary/10 pb-2 last:border-0">
          {item.children && item.children.length > 0 ? <div className="space-y-2">
              <span className="font-bold text-lg font-serif uppercase tracking-wider text-primary block mb-2">
                {item.label}
              </span>
              <div className="pl-4 flex flex-col space-y-3 border-l border-primary/20">
                {item.children.map((child, j) => <Link key={child.id} data-index={j} to={child.href || "#"} className="text-base font-sans text-foreground hover:text-secondary transition-colors block" onClick={onClose}>
                    {child.label}
                  </Link>)}
              </div>
            </div> : <Link to={item.href || "#"} className="block text-lg font-bold font-serif uppercase tracking-wider text-foreground hover:text-secondary transition-colors" onClick={onClose}>
              {item.label}
            </Link>}
        </div>)}
    </nav>;
}
export default function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="site-header" className="w-full flex flex-col font-logo dark bg-background">
      {}
      <div className="w-full h-[40px] bg-primary text-primary-foreground flex items-center justify-end px-4 lg:px-8 text-[11px] font-sans z-50 relative border-b border-primary/10">
        <div className="flex items-center gap-6">
          <Link to="tel:+17025550199" className="flex items-center gap-2 hover:text-secondary transition-colors uppercase tracking-widest font-medium">
            <Phone className="size-3 text-secondary" />
            <span>+1 (405)601-1655</span>
          </Link>
          <Link to="#" className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-1.5 rounded-[5px] font-bold hover:bg-primary/90 transition-colors uppercase tracking-tighter">
            <Mail className="size-3" />
            <span>BOOK VIP EXPERIENCE</span>
          </Link>
        </div>
      </div>

      {}
      <header className={cn("sticky top-0 z-40 w-full transition-all duration-500 border-b", isScrolled ? "bg-background/80 backdrop-blur-[12px] border-primary/10 shadow-lg" : "bg-background border-transparent")}>
        <div className="container mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
          {}
          <Link to="#" className="flex-shrink-0">
            <img src="https://in-antelope.10web.cloud/wp-content/uploads/2026/04/IMG_1405-scaled-e1775586658148.jpg" alt="Platinum Nights" className="h-14 object-contain !w-[98px]" />
          </Link>

          {}
          <div className="hidden lg:flex items-center gap-8">
            <MenuProvider menu_id="45">
              <DesktopMenuConsumer />
            </MenuProvider>

            {}
            <div className="pl-4 border-l border-primary/20">
              
            </div>
          </div>

          {}
          <div className="flex lg:hidden items-center gap-4">
            
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:text-secondary hover:bg-transparent">
              {isMobileMenuOpen ? <X className="size-7" /> : <Menu className="size-7" />}
            </Button>
          </div>
        </div>

        {}
        {isMobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-primary-900 border-b border-primary/20 shadow-2xl animate-in slide-in-from-top-5 duration-300 max-h-[calc(100vh-120px)] overflow-y-auto">
            <MenuProvider menu_id="45">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>}
      </header>
    </section>;
}